import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { Alert, AlertProps, Space, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SetupCheckResult } from '../setup-check'

type SetupCheckResultsProps = {
    result: SetupCheckResult
    successMessage?: string
    warningMessage?: string
}

export function SetupCheckResults({
    result,
    successMessage,
    warningMessage,
    ...rest
}: SetupCheckResultsProps & AlertProps) {
    const { t } = useTranslation()

    return (
        <Alert
            showIcon
            type={result.ok ? 'success' : 'warning'}
            message={
                result.ok ? (successMessage ?? t('Setup looks good')) : (warningMessage ?? t('Setup needs attention'))
            }
            description={
                <Space
                    direction="vertical"
                    size={8}
                    className="w-full"
                >
                    {result.checks.map((check) => (
                        <Space
                            key={check.key}
                            align="start"
                            className="w-full"
                        >
                            {check.ok ? (
                                <CheckCircleOutlined className="text-green-600 mt-1" />
                            ) : (
                                <CloseCircleOutlined className="text-amber-600 mt-1" />
                            )}
                            <Typography.Text>
                                {t(check.code, {
                                    permissions: check.meta?.permissions?.join(', ') ?? '',
                                })}
                            </Typography.Text>
                        </Space>
                    ))}
                </Space>
            }
            {...rest}
        />
    )
}

/**
 * t('facebook.check.connection.not_connected')
 * t('facebook.check.token.not_connected')
 * t('facebook.check.permissions.not_connected')
 * t('facebook.check.accounts.not_connected')
 * t('facebook.check.connection.connected')
 * t('facebook.check.token.valid')
 * t('facebook.check.token.invalid')
 * t('facebook.check.permissions.unverified')
 * t('facebook.check.accounts.unverified')
 * t('facebook.check.permissions.valid')
 * t('facebook.check.permissions.missing', { permissions: '' })
 * t('facebook.check.accounts.accessible')
 * t('facebook.check.accounts.none_found')
 * t('facebook.check.accounts.fetch_failed')
 * t('facebook.lead_check.connection.not_connected')
 * t('facebook.lead_check.account.not_connected')
 * t('facebook.lead_check.form.not_connected')
 * t('facebook.lead_check.lead_access.not_connected')
 * t('facebook.lead_check.webhook.not_connected')
 * t('facebook.lead_check.connection.connected')
 * t('facebook.lead_check.account.missing')
 * t('facebook.lead_check.form.missing_after_account')
 * t('facebook.lead_check.lead_access.missing_page_and_form')
 * t('facebook.lead_check.webhook.missing_page')
 * t('facebook.lead_check.account.selected')
 * t('facebook.lead_check.form.missing')
 * t('facebook.lead_check.lead_access.missing_form')
 * t('facebook.lead_check.webhook.missing_form')
 * t('facebook.lead_check.account.accessible')
 * t('facebook.lead_check.account.inaccessible')
 * t('facebook.lead_check.form.page_inaccessible')
 * t('facebook.lead_check.lead_access.page_inaccessible')
 * t('facebook.lead_check.webhook.page_inaccessible')
 * t('facebook.lead_check.form.accessible')
 * t('facebook.lead_check.form.inaccessible')
 * t('facebook.lead_check.lead_access.confirmed')
 * t('facebook.lead_check.lead_access.no_leads_yet')
 * t('facebook.lead_check.lead_access.missing')
 * t('facebook.lead_check.webhook.valid')
 * t('facebook.lead_check.webhook.invalid')
 * t('facebook.lead_check.fields.not_connected')
 * t('facebook.lead_check.fields.missing_page_and_form')
 * t('facebook.lead_check.fields.missing_form')
 * t('facebook.lead_check.fields.page_inaccessible')
 * t('facebook.lead_check.fields.form_inaccessible')
 * t('facebook.lead_check.fields.valid')
 * t('facebook.lead_check.fields.missing_phone')
 * t('facebook.lead_check.fields.missing_name')
 * t('facebook.lead_check.fields.missing_phone_and_name')
 * t('facebook.lead_check.fields.custom_phone')
 */
